/* Vault — product page: gallery, zoom, lightbox, variants, sticky purchase bar */
(() => {
  'use strict';
  const { qs, qsa, parseHTML } = window.TC;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------------------------------------------------------------- */
  /* Gallery                                                                */
  /* ---------------------------------------------------------------------- */
  class TcGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-tc-track]', this);
      this.slides = qsa('.tc-gallery__slide', this.track);
      this.thumbs = qsa('[data-tc-thumb]', this);
      this.counter = qs('[data-tc-gal-current]', this);
      this.lightbox = qs('[data-tc-lightbox]', this);
      this.index = 0;

      let raf;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            if (i !== this.index) this.setActive(i);
          });
        },
        { passive: true }
      );

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.tcThumb))));
      qs('[data-tc-gal-prev]', this)?.addEventListener('click', () => this.goTo(this.index - 1));
      qs('[data-tc-gal-next]', this)?.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.goTo(this.index + 1);
        if (e.key === 'ArrowLeft') this.goTo(this.index - 1);
      });

      qsa('[data-tc-zoom]', this).forEach((btn) => {
        btn.addEventListener('click', () => this.openLightbox(Number(btn.closest('.tc-gallery__slide').dataset.index)));
        if (!fine()) return;
        const img = qs('img', btn);
        btn.addEventListener('pointermove', (e) => {
          const r = btn.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
          btn.classList.add('is-zoomed');
        });
        btn.addEventListener('pointerleave', () => btn.classList.remove('is-zoomed'));
      });

      if (this.lightbox) this.setupLightbox();
    }

    goTo(i, smooth = true) {
      const n = this.slides.length;
      if (!n) return;
      const target = (i + n) % n;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: smooth && !reduced() ? 'smooth' : 'auto' });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) this.goTo(Number(slide.dataset.index));
    }

    setActive(i) {
      this.index = i;
      if (this.counter) this.counter.textContent = i + 1;
      this.thumbs.forEach((t, ti) => {
        const on = ti === i;
        t.classList.toggle('is-active', on);
        if (on) {
          t.setAttribute('aria-current', 'true');
          t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
        } else t.removeAttribute('aria-current');
      });
      // Pause videos that scroll out of view
      this.slides.forEach((s, si) => {
        if (si !== i) qsa('video', s).forEach((v) => v.pause());
      });
    }

    setupLightbox() {
      const lb = this.lightbox;
      this.lbTrack = qs('[data-tc-lb-track]', lb);
      this.lbSlides = qsa('.tc-lightbox__slide', lb);
      this.lbCount = qs('[data-tc-lb-count]', lb);
      const close = () => lb.close();
      qs('[data-tc-lb-close]', lb).addEventListener('click', close);
      qs('[data-tc-lb-prev]', lb).addEventListener('click', () => this.lbStep(-1));
      qs('[data-tc-lb-next]', lb).addEventListener('click', () => this.lbStep(1));
      lb.addEventListener('click', (e) => {
        if (e.target === lb) close();
      });
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.lbStep(1);
        if (e.key === 'ArrowLeft') this.lbStep(-1);
      });
      lb.addEventListener('close', () => {
        window.TC.unlockScroll();
        const slide = this.lbSlides[this.lbIndex];
        if (slide) this.goTo(Number(slide.dataset.index), false);
      });
      this.lbTrack.addEventListener(
        'scroll',
        () => {
          const i = Math.round(this.lbTrack.scrollLeft / Math.max(1, this.lbTrack.clientWidth));
          this.lbIndex = i;
          this.updateLbCount();
        },
        { passive: true }
      );
    }

    openLightbox(mediaIndex) {
      if (!this.lightbox || !this.lbSlides.length) return;
      const pos = Math.max(0, this.lbSlides.findIndex((s) => Number(s.dataset.index) === mediaIndex));
      this.lightbox.showModal();
      window.TC.lockScroll();
      this.lbIndex = pos;
      requestAnimationFrame(() => {
        this.lbTrack.scrollTo({ left: pos * this.lbTrack.clientWidth, behavior: 'auto' });
        this.updateLbCount();
      });
    }

    lbStep(dir) {
      const n = this.lbSlides.length;
      this.lbIndex = (this.lbIndex + dir + n) % n;
      this.lbTrack.scrollTo({ left: this.lbIndex * this.lbTrack.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
      this.updateLbCount();
    }

    updateLbCount() {
      if (this.lbCount) this.lbCount.textContent = `${this.lbIndex + 1} / ${this.lbSlides.length}`;
    }
  }
  if (!customElements.get('tc-gallery')) customElements.define('tc-gallery', TcGallery);

  /* ---------------------------------------------------------------------- */
  /* Product: variants + sticky bar                                         */
  /* ---------------------------------------------------------------------- */
  const initProduct = (root) => {
    if (root.dataset.tcBound) return;
    root.dataset.tcBound = '1';
    const sectionId = root.dataset.sectionId;
    const productUrl = root.dataset.productUrl;
    const picker = qs('[data-tc-variants]', root);
    const gallery = qs('tc-gallery', root);

    if (picker) {
      const variants = JSON.parse(qs('[data-tc-variant-json]', picker).textContent);
      const idInput = qs('[data-tc-variant-id]', root);
      let controller;

      picker.addEventListener('change', async () => {
        const selected = qsa('fieldset', picker).map((fs) => {
          const checked = qs('input:checked', fs);
          return checked ? checked.value : null;
        });
        qsa('[data-tc-option-label]', picker).forEach((el, i) => (el.textContent = selected[i] || ''));
        const variant = variants.find((v) => v.options.every((o, i) => o === selected[i]));
        const atc = qs('[data-tc-atc]', root);
        if (!variant) {
          if (atc) atc.disabled = true;
          return;
        }
        idInput.value = variant.id;
        if (variant.featured_media && gallery) gallery.goToMedia(variant.featured_media.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);

        if (controller) controller.abort();
        controller = new AbortController();
        root.classList.add('is-switching');
        try {
          const res = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`, { signal: controller.signal });
          const doc = parseHTML(await res.text());
          qsa('[data-tc-swap]', root).forEach((el) => {
            const next = qs(`[data-tc-swap="${el.dataset.tcSwap}"]`, doc);
            if (next) el.innerHTML = next.innerHTML;
          });
          const stickyBtn = qs('[data-tc-swap-attr="sticky-btn"]', root);
          const nextSticky = qs('[data-tc-swap-attr="sticky-btn"]', doc);
          if (stickyBtn && nextSticky) {
            stickyBtn.disabled = nextSticky.disabled;
            stickyBtn.innerHTML = nextSticky.innerHTML;
          }
          window.TC.init(root);
        } catch (err) {
          /* aborted or offline: keep current UI */
        } finally {
          root.classList.remove('is-switching');
        }
      });
    }

    const bar = qs('[data-tc-sticky-atc]', root);
    const buy = qs('.tc-pdp__buy', root);
    if (bar && buy && 'IntersectionObserver' in window) {
      const btn = qs('button', bar);
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (btn) btn.tabIndex = show ? 0 : -1;
        document.body.classList.toggle('tc-has-sticky-atc', show);
      });
      io.observe(buy);
    }
  };

  const boot = (scope = document) => qsa('[data-tc-product]', scope).forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
